import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { BlogPostSummary } from "~/lib/blog-queries";
import { buildMeta } from "~/lib/seo/meta";
import { breadcrumbSchema, schemaGraph, blogSchema } from "~/lib/seo/schema";
import { JsonLd } from "~/components/seo/JsonLd";
import { ProgrammaticIndex } from "~/components/blog/ProgrammaticIndex";
import { serializeForServer } from "~/lib/server-serialization";
import { getLocale } from "~/paraglide/runtime";
import { LocalizedBlogLink } from "~/components/blog/BlogLink";
import { getBlogCanonical, getBlogHeadLinks } from "~/lib/blog-seo";

const fetchCountryPosts = createServerFn({ method: "GET" })
  .validator(z.string().transform((code) => code.toUpperCase()))
  .handler(async ({ data: code }) => {
    const { listPublishedPosts } = await import("~/lib/blog-queries");
    const locale = getLocale();
    const posts = await listPublishedPosts({ locale, pageType: "country", limit: 50 });
    const filtered = posts.filter((p) => p.countryCode?.toUpperCase() === code);
    return serializeForServer({ code, locale, posts: filtered });
  });

export const Route = createFileRoute("/blog/countries/$code")({
  loader: ({ params }) => fetchCountryPosts({ data: params.code }),
  head: ({ loaderData }) => {
    if (!loaderData || loaderData.posts.length === 0) return {};
    const title = loaderData.posts[0]?.title ?? `Jobs in ${loaderData.code}`;
    const seoPage = { locale: loaderData.locale, pageType: "country", countryCode: loaderData.code };
    return {
      meta: buildMeta({
        title: `${title} | Alchema Blog`,
        description: `Job market guide, salary data, visa info, and career tips for ${loaderData.code}.`,
        url: getBlogCanonical(seoPage).path,
      }),
      links: getBlogHeadLinks(seoPage),
    };
  },
  component: CountryPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-32 text-center" style={{ backgroundColor: "var(--bg)" }}>
      <h1 className="text-2xl font-semibold" style={{ color: "var(--text)" }}>Country not found</h1>
      <LocalizedBlogLink href="/blog" className="mt-6 inline-flex rounded-[10px] px-6 py-2.5 text-sm font-medium" style={{ background: "var(--accent)", color: "var(--text-inverse)" }}>
        View all articles
      </LocalizedBlogLink>
    </div>
  ),
});

function CountryPage() {
  const { code, locale, posts } = Route.useLoaderData();

  if (posts.length === 0) throw notFound();

  // Use first post as the main page, rest as related
  const mainPost = posts[0];
  const relatedPosts = posts.slice(1);

  const jsonLd = schemaGraph(
    blogSchema(locale, getBlogCanonical({ locale, pageType: "country", countryCode: code }).path),
    breadcrumbSchema([
      { name: "Home", url: locale === "en" ? "/" : `/${locale}` },
      { name: "Blog", url: getBlogCanonical({ locale, pageType: "index" }).path },
      { name: `${code} Guide`, url: getBlogCanonical({ locale, pageType: "country", countryCode: code }).path },
    ]),
  );

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={jsonLd} />
      <ProgrammaticIndex
        title={mainPost.title}
        subtitle={`Job market, visa requirements, and salary data for ${code}`}
        breadcrumbs={[
          { label: "Blog", href: "/blog" },
          { label: `${code} Guide`, href: `/blog/countries/${code}` },
        ]}
        mainPost={mainPost}
        relatedPosts={relatedPosts}
        pageType="country"
      />
    </div>
  );
}
